import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { cn } from "@/lib/utils"

interface ContactFormProps {
  title: string
  subtitle: string
  labels: {
    name: string
    name_placeholder: string
    email: string
    email_placeholder: string
    message: string
    message_placeholder: string
    submit: string
  }
}

interface PageProps {
  flash: { notice?: string; alert?: string }
  errors?: Record<string, string | string[]>
  [key: string]: unknown
}

export default function ContactForm({ title, subtitle, labels }: ContactFormProps) {
  const { props } = usePage<PageProps>()
  const flash = props.flash ?? {}
  // Annotated: without it the `?? {}` widens the type to a union with `{}`,
  // whose entries are `never`, and `v.length` below stops typechecking.
  const inertiaErrors: Record<string, string | string[]> = props.errors ?? {}

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    content: "",
    nickname: "",
  })
  const [submitting, setSubmitting] = useState(false)
  const [localSuccess, setLocalSuccess] = useState(false)

  const errors: Record<string, string> = Object.fromEntries(
    Object.entries(inertiaErrors)
      .map(([k, v]) => {
        if (Array.isArray(v)) return v.length > 0 ? [k, v[0]] : null
        if (typeof v === "string" && v.length > 0) return [k, v]
        return null
      })
      .filter((entry): entry is [string, string] => entry !== null)
  )

  const hasSuccess = localSuccess || Boolean(flash.notice)
  const hasError = Boolean(flash.alert) && !hasSuccess

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    router.post(
      "/send_message",
      { contact_form: form },
      {
        onSuccess: () => {
          setLocalSuccess(true)
          setForm({ full_name: "", email: "", content: "", nickname: "" })
        },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  const inputClass = (field: keyof typeof form) =>
    cn(
      "w-full px-4 py-3 border bg-light focus:ring-0 transition-colors",
      errors[field] ? "border-red-500" : "border-contour focus:border-contrast"
    )

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-contrast mb-4">{title}</h2>
            <div className="w-16 h-px bg-contrast mx-auto mb-6" />
            <p className="text-content">{subtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="hidden">
              <input
                type="text"
                name="contact_form[nickname]"
                value={form.nickname}
                onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="contact-full-name"
                  className="block text-sm text-contrast mb-2"
                >
                  {labels.name}
                </label>
                <input
                  id="contact-full-name"
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder={labels.name_placeholder}
                  required
                  className={inputClass("full_name")}
                />
                {errors.full_name && (
                  <p className="mt-1 text-xs text-red-500">{errors.full_name}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="contact-email"
                  className="block text-sm text-contrast mb-2"
                >
                  {labels.email}
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={labels.email_placeholder}
                  required
                  className={inputClass("email")}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="contact-message"
                className="block text-sm text-contrast mb-2"
              >
                {labels.message}
              </label>
              <textarea
                id="contact-message"
                rows={6}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={labels.message_placeholder}
                required
                className={cn(inputClass("content"), "resize-none")}
              />
              {errors.content && (
                <p className="mt-1 text-xs text-red-500">{errors.content}</p>
              )}
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={submitting}
                className="px-12 py-4 bg-contrast text-light font-medium tracking-wide hover:bg-secondary transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? `${labels.submit}…` : labels.submit}
              </button>
            </div>

            {hasSuccess && (
              <p className="text-center text-sm text-green-600 font-medium">
                ✓ {flash.notice ?? "Message envoyé."}
              </p>
            )}
            {hasError && (
              <p className="text-center text-sm text-red-500">{flash.alert}</p>
            )}
          </form>
        </div>
      </div>
    </section>
  )
}
